import { CONTACTS } from '@department-of-veterans-affairs/component-library/contacts';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { focusElement } from 'platform/utilities/ui';

import ChatMessageContent from './ChatMessageContent';
import ChatMessageIcon from './ChatMessageIcon';
import ChatMessageOptions from './ChatMessageOptions';
import {
  AI_DISCLAIMER_TEXT_AFTER_PHONE,
  AI_DISCLAIMER_TEXT_BEFORE_PHONE,
} from '../../utils/aiDisclaimerConstants';
import { OPTION_TYPES } from '../../utils/directLineActivity';
import { SENDER_TYPES } from '../../utils/senderTypes';

// Interactive controls a message may contain (option buttons, content links,
// the AI disclaimer's phone link). Web components are matched by host, since
// their control sits in a shadow root where querySelectorAll can't reach it.
const INTERACTIVE_CONTROL_SELECTOR =
  'button, va-button, va-telephone, [role="button"], a[href], input, select, textarea';

// Where focus goes inside each web component's shadow root.
const SHADOW_FOCUS_SELECTORS = { 'VA-BUTTON': 'button', 'VA-TELEPHONE': 'a' };

/**
 * @typedef {Object} ChatMessage
 * @property {string} id
 * @property {string} sender
 * @property {string} text
 * @property {string[]} [options]
 * @property {'icon'|'text'} [optionType]
 * @property {string} [feedbackTargetMessageId]
 * @property {boolean} [feedbackSubmitted]
 * @property {boolean} [feedbackPending]
 */

/**
 * @typedef {Object} ChatMessageItemProps
 * @property {ChatMessage} message
 * @property {(value: string, optionType?: string, message?: ChatMessage) => void} [onOptionSelect]
 * @property {boolean} [showAvatar]
 * @property {boolean} [showAiDisclaimer]
 */

const buildBubbleClassNames = isUser => {
  return classNames('vads-u-padding-y--1 vads-u-padding-x--1p5', {
    'vads-u-background-color--primary-alt-lightest vads-u-border-color--primary':
      !isUser,
    'vads-u-background-color--gray-lightest vads-u-margin-left--auto': isUser,
  });
};

/**
 * Chat message entry item.
 * @component
 * @param {ChatMessageItemProps} props
 * @returns {JSX.Element}
 */
export default function ChatMessageItem({
  index = 0,
  isActive = false,
  isTabStop = false,
  message,
  onActivate,
  onExitNavigation,
  onMove,
  onOptionSelect,
  showAvatar = true,
  showAiDisclaimer = false,
}) {
  const itemRef = React.useRef(null);
  const isUser = message.sender === SENDER_TYPES.USER;
  const contentId = `chat-message-content-${message.id}`;
  const speakerId = `chat-message-speaker-${message.id}`;
  const speakerPrefix = isUser ? 'You said' : 'VA chatbot said';
  const messageOptions = Array.isArray(message.options) ? message.options : [];
  const isIconOptions =
    message.optionType === OPTION_TYPES.ICON && messageOptions.length > 0;
  // Only RAG (LLM) generated answers carry the AI disclaimer, and only when the
  // feature toggle is on. Announce it as part of the message for screen readers.
  const shouldShowDisclaimer =
    !isUser && Boolean(message.isAiGenerated) && showAiDisclaimer;

  // ChatMessageOptions doesn't know which message it's attached to. Closing
  // over `message` here lets handlers identify the source without threading
  // metadata through ChatMessageOptions' click handlers.
  const handleOptionSelect = React.useCallback(
    (value, optionType) => {
      onOptionSelect?.(value, optionType, message);
    },
    [message, onOptionSelect],
  );

  useEffect(() => {
    if (isActive && itemRef.current) {
      itemRef.current.focus();
    }
  }, [isActive]);

  // Keep links and buttons inside messages out of the page's tab order.
  // Users reach them by pressing Enter on a message, then Tab between them.
  useEffect(() => {
    itemRef.current
      ?.querySelectorAll(INTERACTIVE_CONTROL_SELECTOR)
      .forEach(control => control.setAttribute('tabindex', '-1'));
  });

  const getInteractiveControls = () =>
    Array.from(
      itemRef.current?.querySelectorAll(INTERACTIVE_CONTROL_SELECTOR) ?? [],
    ).filter(control => !control.disabled);

  const focusControl = control => {
    const shadowSelector = SHADOW_FOCUS_SELECTORS[control.tagName];
    if (shadowSelector) {
      focusElement(shadowSelector, {}, control);
    } else {
      control.focus();
    }
  };

  // Only engage navigation from the item itself; events bubbling from its
  // controls would let incoming messages steal focus.
  const handleActivate = event => {
    if (event.target?.closest?.(INTERACTIVE_CONTROL_SELECTOR)) {
      return;
    }
    onActivate?.(index);
  };

  const handleKeyDown = event => {
    const controlTarget = event.target?.closest?.(INTERACTIVE_CONTROL_SELECTOR);

    // Focus is on a link or button inside the message: Tab cycles the
    // message's controls and Escape steps back to the message itself.
    if (controlTarget) {
      if (event.key === 'Tab') {
        const controls = getInteractiveControls();
        const currentIndex = controls.indexOf(controlTarget);
        if (currentIndex < 0) {
          return;
        }
        event.preventDefault();
        const step = event.shiftKey ? -1 : 1;
        const nextIndex =
          (currentIndex + step + controls.length) % controls.length;
        focusControl(controls[nextIndex]);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        itemRef.current?.focus();
        return;
      }

      if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      onMove?.(index - 1);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onMove?.(index + 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      onMove?.(0);
      return;
    }

    if (event.key === 'End') {
      // The parent clamps this to the last message.
      event.preventDefault();
      onMove?.(Infinity);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onExitNavigation?.();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      const [firstControl] = getInteractiveControls();
      if (firstControl) {
        event.preventDefault();
        focusControl(firstControl);
      }
    }
  };

  // TODO: not sure on the a11y of having li elements having mouse or keyboard events
  // on the list items. eslint is flagging it, but don't see a lot of other options to
  // allow navigation for chat messages via keyboard
  return (
    // No aria-labelledby: naming the message after its own content made
    // VoiceOver replay the whole response whenever focus entered the
    // disclaimer's phone link.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <li
      aria-roledescription="message"
      data-chat-message-index={index}
      className="vads-u-display--flex vads-u-align-items--flex-start vads-u-margin-bottom--1p5"
      data-testid="chat-message-item"
      onClick={handleActivate}
      onFocus={handleActivate}
      onKeyDown={handleKeyDown}
      ref={itemRef}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isTabStop ? 0 : -1}
    >
      <span className="sr-only" id={speakerId}>
        {speakerPrefix}
      </span>
      {!isUser && (
        <div className="vads-u-margin-right--1">
          {showAvatar ? (
            <ChatMessageIcon user={isUser} />
          ) : (
            <span
              aria-hidden="true"
              className="vads-u-display--inline-block"
              style={{ width: '2rem' }}
            />
          )}
        </div>
      )}

      <div className={buildBubbleClassNames(isUser)}>
        <div
          className={
            isIconOptions ? 'va-chatbot-message-with-icon-options' : undefined
          }
        >
          <ChatMessageContent
            contentId={contentId}
            isUser={isUser}
            text={message.text}
          />
          {!isUser ? (
            <ChatMessageOptions
              areIconOptionsDisabled={
                Boolean(message.feedbackSubmitted) ||
                Boolean(message.feedbackPending)
              }
              messageId={message.id}
              onOptionSelect={handleOptionSelect}
              optionType={message.optionType}
              options={messageOptions}
            />
          ) : null}
          {shouldShowDisclaimer ? (
            <p
              className="va-chatbot-ai-disclaimer vads-u-margin-top--1 vads-u-margin-bottom--0"
              data-testid="chat-message-ai-disclaimer"
            >
              {AI_DISCLAIMER_TEXT_BEFORE_PHONE}
              <va-telephone contact={CONTACTS.VA_411} />
              {AI_DISCLAIMER_TEXT_AFTER_PHONE}
            </p>
          ) : null}
        </div>
      </div>

      {isUser && (
        <div className="vads-u-margin-left--1">
          <ChatMessageIcon user={isUser} />
        </div>
      )}
    </li>
  );
}

ChatMessageItem.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sender: PropTypes.oneOf([SENDER_TYPES.USER, SENDER_TYPES.BOT]).isRequired,
    text: PropTypes.string.isRequired,
    feedbackPending: PropTypes.bool,
    feedbackSubmitted: PropTypes.bool,
    feedbackTargetMessageId: PropTypes.string,
    isAiGenerated: PropTypes.bool,
    optionType: PropTypes.oneOf(['icon', 'text']),
    options: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  index: PropTypes.number,
  isActive: PropTypes.bool,
  isTabStop: PropTypes.bool,
  showAiDisclaimer: PropTypes.bool,
  showAvatar: PropTypes.bool,
  onActivate: PropTypes.func,
  onExitNavigation: PropTypes.func,
  onMove: PropTypes.func,
  onOptionSelect: PropTypes.func,
};
